import React, { useState, useEffect, useCallback, useRef } from 'react';
import comms from '../../comms';

const formatTimeHHMM = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date)) {
    return "00:00";
  }
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

const LoopedCurrentWaypoint = ({ 
  waypoint, 
  expectedTime, 
  isAccurate, 
  isPhone, 
  vehiclePosition = null,
  currentLocation = null,
  currentRun = null, // Optional fallback for run information
  loopedRouteData = null, // Optional fallback for routes
  selectedVehicle,
  routeDetails,
  userSettings, // User preferences and settings
  breakLogs = [], // Server-authoritative break logs
  endPointLog = null, // Server-authoritative endpoint log
  onArrive = null, // Callback when arrive button is pressed
  onEndpointCompleted = null, // Callback when endpoint is completed
}) => {
  // Stop status: 'arriving', 'passengersOut', 'passengersIn'
  const [stopStatus, setStopStatus] = useState('arriving');
  const [passengersOut, setPassengersOut] = useState('');
  const [passengersIn, setPassengersIn] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [breakTimeRemaining, setBreakTimeRemaining] = useState(null);
  const [canDepart, setCanDepart] = useState(true);
  const inputRef = useRef(null);

  // Get userSettings with localStorage fallback
  const getUserSettings = useCallback(() => {
    if (userSettings && Object.keys(userSettings).length > 0) {
      return userSettings;
    }
    
    if (typeof window !== 'undefined') {
      try {
        const storedSettings = localStorage.getItem('userSettings');
        if (storedSettings) {
          const parsedSettings = JSON.parse(storedSettings);
          if (parsedSettings && typeof parsedSettings === 'object') {
            return parsedSettings;
          }
        }
      } catch (error) {
        console.error('Error loading userSettings from localStorage:', error);
      }
    }
    
    return null;
  }, [userSettings]);

  // Helper function to get run information with fallbacks
  const getRunInfo = useCallback(() => {
    // First try: routeDetails (from authorization response routeObject)
    if (routeDetails?.runId && routeDetails?.runName) {
      return {
        runName: routeDetails.runName || '',
        runId: routeDetails.runId || ''
      };
    }
    
    // Second try: waypoint.run (should be available after loopedSocketHelper enhancement)
    if (waypoint?.run) {
      return {
        runName: waypoint.run.Name || '',
        runId: waypoint.run.Id || ''
      };
    }
    
    // Third try: currentRun prop
    if (currentRun) {
      return {
        runName: currentRun.Name || '',
        runId: currentRun.Id || ''
      };
    }
    
    // Fourth try: loopedRouteData
    if (loopedRouteData?.Runs && Array.isArray(loopedRouteData.Runs) && loopedRouteData.Runs.length > 0) {
      const firstRun = loopedRouteData.Runs[0];
      return {
        runName: firstRun.Name || '',
        runId: firstRun.Id || ''
      };
    }
    
    // Fallback: empty values
    console.warn('No run information available for looped current waypoint');
    return {
      runName: '',
      runId: ''
    };
  }, [waypoint, currentRun, loopedRouteData, routeDetails]);


  // Get active break for current waypoint using breakStep flag
  const getCurrentWaypointBreak = useCallback(() => {
    // Use waypoint.breakStep flag to determine if this is a break stop
    if (!waypoint?.breakStep || !breakLogs || breakLogs.length === 0) return null;
    
    // Use consistent ID property (server ensures waypoint.Id is standardized)
    const waypointId = waypoint.Id || waypoint.StopId || waypoint.ItineraryId;
    if (!waypointId) return null;
    
    // Find break log for current waypoint - should be the first (most recent) one
    return breakLogs.find(log => 
      log.StopId === waypointId || log.FixedRoute_StopId === waypointId
    ) || null;
  }, [waypoint, breakLogs]);

  // Check if current waypoint is an endpoint using endStep flag
  const isCurrentWaypointEndpoint = useCallback(() => {
    // Use waypoint.endStep flag to determine if this is an endpoint
    return waypoint?.endStep === true;
  }, [waypoint]);



  // Helper function to parse various time formats to Date object
  const parseTimeToDate = useCallback((timeValue) => {
    if (!timeValue) return null;
    
    // If it's already a Date object
    if (timeValue instanceof Date) return timeValue;
    
    // If it's an ISO string (like "2024-01-01T14:30:00")
    if (typeof timeValue === 'string' && timeValue.includes('T')) {
      return new Date(timeValue);
    }
    
    // If it's a time string (like "14:30" or "2:30 PM")
    if (typeof timeValue === 'string') {
      const today = new Date();
      const [hours, minutes] = timeValue.includes(':') ? 
        timeValue.split(':').map(Number) : [0, 0];
      
      if (!isNaN(hours) && !isNaN(minutes)) {
        today.setHours(hours, minutes, 0, 0);
        return today;
      }
    }
    
    return null;
  }, []);

  // Calculate break time remaining using server break data
  const checkBreakTimeRemaining = useCallback(() => {
    // Only calculate break time if this is a break step
    if (!waypoint?.breakStep) return null;
    
    const currentBreak = getCurrentWaypointBreak();
    if (!currentBreak || !currentBreak.DepartureTime) return null;
    
    // Parse the DepartureTime from the break log
    const breakEndTime = parseTimeToDate(currentBreak.DepartureTime);
    if (!breakEndTime) return null;
    
    const now = new Date();
    const timeDiff = breakEndTime.getTime() - now.getTime();
    
    // Return seconds remaining (or 0 if break time has passed)
    return Math.max(0, Math.ceil(timeDiff / 1000));
  }, [waypoint, getCurrentWaypointBreak, parseTimeToDate]);

  // Update time-based states every 10 seconds
  useEffect(() => {
    const updateTimeStates = () => {
      const currentBreak = getCurrentWaypointBreak();
      const isEndpoint = isCurrentWaypointEndpoint();
      const isBreakStep = waypoint?.breakStep === true;
      const isEndStep = waypoint?.endStep === true;
      
      // Check break time remaining (only for break steps)
      const breakRemaining = checkBreakTimeRemaining();
      setBreakTimeRemaining(breakRemaining);
      
      // Update canDepart based on step type and conditions
      if (isBreakStep && breakRemaining !== null && breakRemaining > 0) {
        // Break step: cannot depart until break time has elapsed
        setCanDepart(false);
      } else if (isEndStep) {
        // End step: departure completes route and directs to inspection/lot return
        setCanDepart(true);
      } else {
        // Normal step: departure allowed
        setCanDepart(true);
      }
    };

    // Initial check
    updateTimeStates();
    
    // Set up 10-second interval
    const interval = setInterval(updateTimeStates, 10000);
    
    return () => clearInterval(interval);
  }, [waypoint, stopStatus, getCurrentWaypointBreak, isCurrentWaypointEndpoint, checkBreakTimeRemaining]);

  // Focus input when entering passenger entry phases
  useEffect(() => {
    if ((stopStatus === 'passengersOut' || stopStatus === 'passengersIn') && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 100);
    }
  }, [stopStatus]);

  // Send departure message to server
  const sendDepartureMessage = () => {
        // Use consistent ID property (server ensures waypoint.Id is standardized)
        const stopId = waypoint.Id || waypoint.StopId || waypoint.ItineraryId;
        if (!stopId) {
      console.error('No valid stop ID found for waypoint:', waypoint);
          return;
        }
        
    const currentUserSettings = getUserSettings();
        const { runName, runId } = getRunInfo();

    const paxOut = passengersOut === '' ? 0 : parseInt(passengersOut);
    const paxIn = passengersIn === '' ? 0 : parseInt(passengersIn);

        const messageData = {
          stopId: stopId,
      passengerCount: paxIn,  // PAXBoarded
      passengersAlighted: paxOut,  // PAXAlighted
      itineraryId: waypoint.ItineraryId || routeDetails.itinerary[0]?.ItineraryId || null,
          runName: routeDetails.runName,
          scheduleId: routeDetails.scheduleId,
          runId: routeDetails.runId,
      driverId: currentUserSettings?.Id || null,
      vehicleId: selectedVehicle?.Id || selectedVehicle?.id || currentUserSettings?.VehicleID || null,
          routeId: routeDetails?.routeId || null,
        };
        
        const message = `FIXEDSTOP_DEPART|${JSON.stringify(messageData)}`;
        comms.sendSocket(message);
        
        // Reset for next stop
        setStopStatus('arriving');
    setPassengersOut('');
    setPassengersIn('');
    setIsProcessing(false);
  };

  const handleArriveAtStop = async () => {
    if (isProcessing) return;
    
    const currentUserSettings = getUserSettings();
    setIsProcessing(true);
    
    try {
      // Send arrival message for looped route using the expected format
      if (waypoint) {
        // Use consistent ID property (server ensures waypoint.Id is standardized)
        const stopId = waypoint.Id || waypoint.StopId || waypoint.ItineraryId;
        if (!stopId) {
          console.error('No valid stop ID found for waypoint:', waypoint);
          return;
        }
        const { runName, runId } = getRunInfo();

        // Send message in the format expected by the sendSocket function: command|data
        
        const messageData = {
          stopId: stopId,
          passengerCount: 0,
          itineraryId: waypoint.ItineraryId || routeDetails.itinerary[0]?.ItineraryId || null,
          runName: routeDetails.runName,
          scheduleId: routeDetails.scheduleId,
          runId: routeDetails.runId,
          driverId: currentUserSettings?.Id || null,
          vehicleId: selectedVehicle?.Id || selectedVehicle?.id || currentUserSettings?.VehicleID || null,
          routeId: routeDetails?.routeId || null,
        };
        const message = `FIXEDSTOP_ARRIVE|${JSON.stringify(messageData)}`;
        comms.sendSocket(message);
        
        // Transition to passengers out phase
        setStopStatus('passengersOut');
        
        // Call the onArrive callback to notify parent component
        if (onArrive && typeof onArrive === 'function') {
          onArrive();
        }
      }
    } catch (error) {
      console.error('Error processing looped route arrival:', error);
      alert('Failed to process action. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleNextToPassengersIn = () => {
    setStopStatus('passengersIn');
  };

  const handleBackToPassengersOut = () => {
    setStopStatus('passengersOut');
  };

  const handleDepart = () => {
        // Check step type using waypoint flags
        const isEndStep = waypoint?.endStep === true;
        const isBreakStep = waypoint?.breakStep === true;
        
        if (isEndStep) {
          handleEndpointCompletion();
          return;
        }
        
        // Check if break time is active for break steps
        if (isBreakStep && breakTimeRemaining > 0) {
          alert(`Please wait ${Math.ceil(breakTimeRemaining / 60)} more minute(s) before departing from break.`);
          return;
        }
        
    setIsProcessing(true);
    sendDepartureMessage();
  };

  // Handle endpoint completion - direct driver to return to lot
  const handleEndpointCompletion = () => {
    const currentUserSettings = getUserSettings();
    
    // Send endpoint completion message using endPointLog data if available
    if (waypoint && endPointLog) {
      const stopId = waypoint.Id || waypoint.StopId || waypoint.ItineraryId;
      const driverId = currentUserSettings?.Id || null;
      const vehicleId = selectedVehicle?.Id || selectedVehicle?.id || currentUserSettings?.VehicleID || null;
      const { runName, runId } = getRunInfo();

      const messageData = {
        stopId: stopId,
        runName: runName,
        runId: runId,
        driverId: driverId,
        vehicleId: vehicleId,
        itineraryId: waypoint.ItineraryId || routeDetails.itinerary[0]?.ItineraryId || null,
        scheduleId: routeDetails.scheduleId,
        routeId: routeDetails?.routeId || null,
        endPointLogId: endPointLog.Id || null
      };
 

      const message = `LOOPED_ROUTE_END|${JSON.stringify(messageData)}`;
      comms.sendSocket(message);
    }
    
    // Navigate to CompleteShift component when endpoint is reached
    if (onEndpointCompleted && typeof onEndpointCompleted === 'function') {
      onEndpointCompleted();
    }
  };

  // Increment/decrement helpers for touch-friendly buttons
  const incrementValue = (setter, currentValue) => {
    const current = currentValue === '' ? 0 : parseInt(currentValue);
    if (current < 99) {
      setter(String(current + 1));
    }
  };

  const decrementValue = (setter, currentValue) => {
    const current = currentValue === '' ? 0 : parseInt(currentValue);
    if (current > 0) {
      setter(String(current - 1));
    }
  };

  const handlePassengersOutChange = (e) => {
    const value = e.target.value;
    if (value === '' || (parseInt(value) >= 0 && parseInt(value) <= 99)) {
      setPassengersOut(value);
    }
  };

  const handlePassengersInChange = (e) => {
    const value = e.target.value;
    if (value === '' || (parseInt(value) >= 0 && parseInt(value) <= 99)) {
      setPassengersIn(value);
    }
  };

  if (!waypoint) return <h2>No current stop available</h2>;

  return (
    <div className={`bg-white border rounded-lg shadow-sm h-full flex flex-col ${isPhone ? 'p-1' : 'p-2'}`}>
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <h2 className="text-2xl font-bold text-blue-600">
            {waypoint.Name}
          </h2>
        </div>

        <div className="text-right">
          {/* Show ETA for normal steps */}
          {waypoint.eta && !waypoint?.breakStep && !waypoint?.endStep && (
            <div className="mb-2">
              <div className="text-base text-gray-600">Expected</div>
              <div className="text-xl font-semibold text-green-600">
                {(new Date(waypoint.eta).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }))}
              </div>
            </div>
          )}
          
          {/* Show break information for break steps */}
          {waypoint?.breakStep && getCurrentWaypointBreak() && (
            <div>
              <div className="text-base text-orange-600">Break Until</div>
              <div className="text-xl font-semibold text-orange-600">
                {(() => {
                  const currentBreak = getCurrentWaypointBreak();
                  if (currentBreak?.DepartureTime) {
                    const breakEnd = parseTimeToDate(currentBreak.DepartureTime);
                    return breakEnd ? formatTimeHHMM(breakEnd) : "Break Active";
                  }
                  return "Break Active";
                })()}
              </div>
              {breakTimeRemaining !== null && breakTimeRemaining > 0 && (
                <div className="text-base text-orange-500 mt-1">
                  {Math.ceil(breakTimeRemaining / 60)} min remaining
                </div>
              )}
            </div>
          )}
          
          {/* Show endpoint information for end steps */}
          {waypoint?.endStep && (
            <div>
              <div className="text-base text-red-600">Route End</div>
              <div className="text-xl font-semibold text-red-600">
                Return to Lot
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Arriving State - Show Arrive Button */}
        {stopStatus === 'arriving' && (
          <div className="mt-auto">
            <button
              onClick={handleArriveAtStop}
              disabled={isProcessing || !isAccurate}
              className={`w-full py-3 px-4 rounded-lg text-xl font-semibold text-white transition-all duration-200 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isProcessing ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processing...
                </div>
              ) : (
                'Arrive at Stop'
              )}
            </button>

        {!isAccurate && (
              <div className="text-sm text-red-600 text-center mt-1">
                GPS accuracy required
              </div>
            )}
          </div>
        )}

        {/* Passengers Out Phase */}
        {stopStatus === 'passengersOut' && (
          <div className="mt-auto">
            <div className="text-center mb-2">
              <span className="text-lg font-bold text-red-600">Passengers OUT</span>
            </div>
            
            <div className="flex items-center justify-center space-x-3 mb-2">
              <button
                onClick={() => decrementValue(setPassengersOut, passengersOut)}
                className="w-12 h-12 rounded-full bg-gray-100 text-gray-700 text-2xl font-bold hover:bg-gray-200 transition-colors flex items-center justify-center border border-gray-300"
                type="button"
              >
                −
              </button>
              
              <input
                ref={inputRef}
                type="number"
                min="0"
                max="99"
                value={passengersOut}
                onChange={handlePassengersOutChange}
                placeholder="0"
                className="w-20 h-12 text-center text-2xl font-bold border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              
              <button
                onClick={() => incrementValue(setPassengersOut, passengersOut)}
                className="w-12 h-12 rounded-full bg-gray-100 text-gray-700 text-2xl font-bold hover:bg-gray-200 transition-colors flex items-center justify-center border border-gray-300"
                type="button"
              >
                +
              </button>
            </div>
            <div className="text-base flex justify-center space-x-4 mb-2">
              <span><span className="text-gray-500">&nbsp;</span></span>
            </div>
            <button
              onClick={handleNextToPassengersIn}
              className="w-full py-2 px-4 rounded-lg text-lg font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              Next →
            </button>
          </div>
        )}

        {/* Passengers In Phase */}
        {stopStatus === 'passengersIn' && (
          <div className="mt-auto">
            <div className="text-center mb-2">
              <span className="text-lg font-bold text-green-600">Passengers IN</span>
            </div>
            
            <div className="flex items-center justify-center space-x-3 mb-2">
              <button
                onClick={() => decrementValue(setPassengersIn, passengersIn)}
                className="w-12 h-12 rounded-full bg-gray-100 text-gray-700 text-2xl font-bold hover:bg-gray-200 transition-colors flex items-center justify-center border border-gray-300"
                type="button"
              >
                −
              </button>
              
              <input
                ref={inputRef}
                type="number"
                min="0"
                max="99"
                value={passengersIn}
                onChange={handlePassengersInChange}
                placeholder="0"
                className="w-20 h-12 text-center text-2xl font-bold border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              
              <button
                onClick={() => incrementValue(setPassengersIn, passengersIn)}
                className="w-12 h-12 rounded-full bg-gray-100 text-gray-700 text-2xl font-bold hover:bg-gray-200 transition-colors flex items-center justify-center border border-gray-300"
                type="button"
              >
                +
              </button>
            </div>

            {/* Summary */}
            <div className="text-base flex justify-center space-x-4 mb-2">
              <span><span className="text-gray-500">Out:</span> <span className="font-semibold text-red-600">{passengersOut || '0'}</span></span>
              <span><span className="text-gray-500">In:</span> <span className="font-semibold text-green-600">{passengersIn || '0'}</span></span>
      </div>

            <div className="flex space-x-2">
              <button
                onClick={handleBackToPassengersOut}
                className="flex-1 py-2 px-3 bg-gray-100 text-gray-700 text-lg font-semibold rounded-lg hover:bg-gray-200 transition-colors border border-gray-300"
              >
                ← Back
              </button>
              <button
                onClick={handleDepart}
                disabled={isProcessing || !canDepart}
                className={`flex-1 py-2 px-3 text-lg font-semibold text-white rounded-lg transition-colors ${
                  waypoint?.endStep 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-green-600 hover:bg-green-700'
                } disabled:bg-gray-400 ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isProcessing ? 'Processing...' : (waypoint?.endStep ? 'Complete Route' : 'Depart')}
              </button>
            </div>

            {waypoint?.breakStep && breakTimeRemaining > 0 && (
              <div className="text-sm text-orange-600 text-center mt-1">
                Break: {Math.ceil(breakTimeRemaining / 60)} min remaining
              </div>
            )}
          </div>
        )}
    </div>
  );
};


export default LoopedCurrentWaypoint;
